import { FilterBar, FilterBarField } from "design-system/molecules/FilterBar"
import { useCampaignsQuery } from "modules/campaigns/hooks/useCampaignsQuery"
import { useFilterBarLabels } from "modules/shared/hooks/useFilterBarLabels"
import { useTranslation } from "react-i18next"
import { Route as GoalsManagementRoute } from "routes/_base/_auth/campaigns/goals/list/route"
import { GoalTypeEnum } from "schema"
import { CreateGoalButton } from "./CreateGoalButton"

type Search = ReturnType<typeof GoalsManagementRoute.useSearch>
type GoalsFilterBy = NonNullable<Search["filterBy"]>

export function GoalsFilter() {
  const { t } = useTranslation()
  const search = GoalsManagementRoute.useSearch()
  const navigate = GoalsManagementRoute.useNavigate()
  const labels = useFilterBarLabels()

  const { data: campaigns = [] } = useCampaignsQuery({
    query: {
      page: 1,
      total: 10,
      sortBy: { field: "createdAt", order: "desc" },
    },
  })

  function onChangeFilter(changes: Partial<GoalsFilterBy>) {
    navigate({
      search: (previous) => ({
        ...previous,
        page: 1,
        filterBy: { ...previous.filterBy, ...changes },
      }),
    })
  }

  function onClearFilters() {
    navigate({ search: (previous) => ({ ...previous, page: 1, filterBy: {}, query: undefined }) })
  }

  const typeLabels = {
    Open: t("campaigns.goals.open"),
    Informative: t("campaigns.goals.informative"),
    Predefined: t("campaigns.goals.predefined"),
    Classification: t("campaigns.goals.classification"),
    NPS: t("campaigns.goals.npsLike"),
  } satisfies Record<keyof typeof GoalTypeEnum, string>

  const fields: FilterBarField[] = [
    {
      key: "type",
      type: "multiSelect",
      label: t("campaigns.goalsManagement.table.type"),
      value: search.filterBy?.type,
      onChange: (values) => {
        const types = Object.values(GoalTypeEnum).filter((type) => values?.includes(type))
        onChangeFilter({ type: types.length > 0 ? types : undefined })
      },
      options: Object.values(GoalTypeEnum).map((type) => ({
        value: type,
        label: typeLabels[type],
      })),
    },
    {
      key: "campaignId",
      type: "multiSelect",
      label: t("campaigns.goalsManagement.campaign"),
      value: search.filterBy?.campaignId,
      onChange: (values) => onChangeFilter({ campaignId: values }),
      options: campaigns.map((campaign) => ({
        value: campaign.id,
        label: campaign.name || t("campaigns.untitled"),
      })),
    },
  ]

  return (
    <FilterBar
      className="w-full"
      fields={fields}
      labels={labels}
      onClearFilters={onClearFilters}
      searchQuery={search.query}
      onSearchQueryChange={(query) =>
        navigate({ search: (previous) => ({ ...previous, query: query || undefined, page: 1 }) })
      }
      searchPlaceholder={t("campaigns.goalsManagement.search")}
      trailing={<CreateGoalButton />}
    />
  )
}
